import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { 
  ArrowLeft, 
  Plus, 
  Copy, 
  Download, 
  ShoppingCart,
  Loader2,
  Trash2,
  ExternalLink,
  ChevronDown,
  Store,
  Check,
  SkipForward,
  X,
  Play,
  ArrowRight,
  Sparkles,
  Zap,
  Calendar,
  ListChecks,
  CheckCircle2,
  PartyPopper,
  Crown,
  ShoppingBag,
  Package,
  Lock,
  Mic,
  MicOff,
  Volume2,
  Lightbulb,
  ChevronUp
} from 'lucide-react';
import UpgradeModal from '@/components/UpgradeModal';
import MobileGroceryList from '@/components/MobileGroceryList';
import { 
  trackDemoStepViewed, 
  trackDemoStepCompleted, 
  trackExportClicked, 
  trackExportCompleted,
  trackSignupModalShown,
  trackGroceryItemChecked,
  trackCheckoutClicked
} from '@/utils/analytics';
import { haptic } from '@/utils/haptics';

const GroceryListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, isAuthenticated } = useAuth();
  const isDemoMode = searchParams.get('demo') === 'true';
  const autoExport = searchParams.get('autoExport') === 'true';
  const justUpgraded = searchParams.get('upgraded') === 'true';
  const demoGenerated = searchParams.get('generated') === 'true';
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Track demo step 3/4 viewed
  useEffect(() => {
    if (isDemoMode) {
      if (demoGenerated) {
        trackDemoStepViewed(3, 'View Grocery List');
      } else {
        trackDemoStepViewed(4, 'Export to Store');
      }
    }
  }, [isDemoMode, demoGenerated]);
  
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemUnit, setNewItemUnit] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(null);
  
  // Shopping Mode State (online)
  const [shoppingMode, setShoppingMode] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [selectedStore, setSelectedStore] = useState('instacart');
  const [addedItems, setAddedItems] = useState(new Set());
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // In-Store Shopping Mode State (Pro Plus)
  const [inStoreMode, setInStoreMode] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [isProPlus, setIsProPlus] = useState(false);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  
  // Smart Suggestions State (Pro Plus)
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Skipped Pantry Staples State
  const [skippedStaples, setSkippedStaples] = useState([]);
  const [showSkippedStaples, setShowSkippedStaples] = useState(false);
  
  // Voice Shopping State
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);
  
  // Demo mode state
  const [showDemoUpgradeModal, setShowDemoUpgradeModal] = useState(false);
  const [showFirstTimeTooltip, setShowFirstTimeTooltip] = useState(isDemoMode);
  const [showStep4Complete, setShowStep4Complete] = useState(false);
  const [hasTriedExport, setHasTriedExport] = useState(false);
  
  // Post-upgrade magic moment state
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(justUpgraded);
  const [autoExportTriggered, setAutoExportTriggered] = useState(false);

  // Mock grocery list for demo mode
  const mockDemoList = {
    id: 'demo-list-1',
    name: 'Weekly Groceries',
    created_at: new Date().toISOString(),
    items: [
      { name: 'Chicken breast', amount: 2, unit: 'lbs', aisle: 'Meat', checked: false },
      { name: 'Pasta', amount: 1, unit: 'box', aisle: 'Pantry', checked: false },
      { name: 'Garlic', amount: 4, unit: 'cloves', aisle: 'Produce', checked: false },
      { name: 'Olive oil', amount: 1, unit: 'bottle', aisle: 'Pantry', checked: false },
      { name: 'Parmesan cheese', amount: 1, unit: 'cup', aisle: 'Dairy', checked: false },
      { name: 'Breadcrumbs', amount: 1, unit: 'cup', aisle: 'Pantry', checked: false },
      { name: 'Fresh basil', amount: 1, unit: 'bunch', aisle: 'Produce', checked: false },
      { name: 'Tomatoes', amount: 4, unit: '', aisle: 'Produce', checked: false },
    ]
  };

  const stores = {
    instacart: { 
      name: 'Instacart', 
      color: '#43B02A', 
      baseUrl: 'https://www.instacart.com/store/search/'
    },
    amazon: { 
      name: 'Amazon Fresh', 
      color: '#FF9900', 
      baseUrl: 'https://www.amazon.com/s?i=amazonfresh&k='
    },
    walmart: { 
      name: 'Walmart', 
      color: '#0071CE', 
      baseUrl: 'https://www.walmart.com/search?q='
    }
  };

  const uncheckedItems = list?.items?.filter(item => !item.checked) || [];
  const currentItem = uncheckedItems[currentItemIndex];
  const progress = uncheckedItems.length > 0 
    ? ((addedItems.size) / uncheckedItems.length) * 100 
    : 0;

  // Scroll detection for Step 4 completion in demo mode
  useEffect(() => {
    if (!isDemoMode || !isAuthenticated === false) return;
    
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      
      // If user has scrolled near the bottom (within 200px)
      if (scrollPosition >= pageHeight - 200) {
        setShowStep4Complete(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDemoMode, isAuthenticated]);

  useEffect(() => {
    const fetchList = async () => {
      // Demo mode - use mock data
      if (isDemoMode && !isAuthenticated) {
        setList(mockDemoList);
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/grocery-lists/${id}`);
        setList(response.data);
      } catch (error) {
        console.error('Failed to load grocery list:', error);
        toast.error('Failed to load grocery list');
        navigate('/grocery-lists');
      } finally {
        setLoading(false);
      }
    };

    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, api, navigate, isDemoMode, isAuthenticated]);

  // Check Pro Plus subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await api.get('/subscription/status');
        const isPP = response.data.is_pro && response.data.plan === 'plus';
        setIsProPlus(isPP);
      } catch (error) {
        console.error('Failed to check subscription:', error);
      }
    };
    checkSubscription();
  }, [api, isAuthenticated]);

  // Load smart suggestions for Pro Plus users
  const loadSuggestions = useCallback(async () => {
    if (!isProPlus || !list?.id || isDemoMode) return;
    
    setLoadingSuggestions(true);
    try {
      const response = await api.get(`/grocery-lists/${list.id}/suggestions`);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [api, isProPlus, list?.id, isDemoMode]);

  useEffect(() => {
    if (isProPlus && list?.id && list.items?.length > 0) {
      loadSuggestions();
    }
  }, [isProPlus, list?.id, list?.items?.length, loadSuggestions]);

  // Load skipped pantry staples
  useEffect(() => {
    const loadSkippedStaples = async () => {
      if (!isAuthenticated || isDemoMode) return;
      try {
        const response = await api.get('/pantry/staples');
        const userStaples = response.data.user_staples || response.data.staples || [];
        if (userStaples.length > 0) {
          setSkippedStaples(userStaples);
        }
      } catch (error) {
        // Silently fail - this is optional info
        console.error('Failed to load pantry staples:', error);
      }
    };
    loadSkippedStaples();
  }, [api, isAuthenticated, isDemoMode]);

  const addSuggestionToList = async (suggestion) => {
    if (!list) return;
    
    const newItem = {
      name: suggestion.name,
      amount: 1,
      unit: '',
      aisle: suggestion.category || 'Other',
      checked: false
    };
    
    const updatedItems = [...list.items, newItem];
    
    try {
      await api.put(`/grocery-lists/${list.id}`, {
        name: list.name,
        items: updatedItems
      });
      setList({ ...list, items: updatedItems });
      setSuggestions(suggestions.filter(s => s.name !== suggestion.name));
      toast.success(`Added ${suggestion.name} to list`);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  // Auto-export after upgrade (magic moment)
  useEffect(() => {
    if (autoExport && justUpgraded && !autoExportTriggered && list && isAuthenticated) {
      setAutoExportTriggered(true);
      
      // Clear the URL params but keep showing success state
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('autoExport');
      newParams.delete('upgraded');
      setSearchParams(newParams, { replace: true });
      
      // 🎉 Trigger confetti celebration!
      const triggerConfetti = () => {
        // First burst - center
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#4A6C4C', '#43B02A', '#FFD700', '#FF6B6B', '#4ECDC4']
        });
        
        // Side bursts after a small delay
        setTimeout(() => {
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.6 },
            colors: ['#4A6C4C', '#43B02A', '#FFD700']
          });
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.6 },
            colors: ['#4A6C4C', '#43B02A', '#FFD700']
          });
        }, 150);
      };
      
      // Trigger confetti immediately
      triggerConfetti();
      
      // Show celebratory toast
      toast.success(
        <div className="flex items-center gap-2">
          <PartyPopper className="h-5 w-5 text-yellow-500" />
          <span><strong>Welcome to Pro!</strong> Your grocery list is on its way to Instacart.</span>
        </div>,
        { duration: 5000 }
      );
      
      // Auto-trigger export to Instacart after a brief delay
      setTimeout(async () => {
        try {
          const response = await api.post(`/export/instacart?list_id=${id}`);
          window.open(response.data.url, '_blank');
        } catch (error) {
          console.error('Auto-export error:', error);
          // Still show success, they can manually export
          toast.info('Click "Send to Store" to export your list anytime!');
        }
      }, 2000);
    }
  }, [autoExport, justUpgraded, autoExportTriggered, list, isAuthenticated, api, id, searchParams, setSearchParams]);

  const saveList = async (updatedItems) => {
    // Demo mode - just update state
    if (isDemoMode && !isAuthenticated) {
      setList({ ...list, items: updatedItems });
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put(`/grocery-lists/${id}`, {
        name: list.name,
        items: updatedItems
      });
      setList(response.data);
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = async (index) => {
    const updatedItems = [...list.items];
    updatedItems[index] = {
      ...updatedItems[index],
      checked: !updatedItems[index].checked
    };
    await saveList(updatedItems);
    
    // Hide tooltip after first interaction
    setShowFirstTimeTooltip(false);
  };

  const removeItem = async (index) => {
    const updatedItems = list.items.filter((_, i) => i !== index);
    await saveList(updatedItems);
    toast.success('Item removed');
  };

  const addItem = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter an item name');
      return;
    }

    const newItem = {
      name: newItemName.trim(),
      amount: parseFloat(newItemAmount) || 0,
      unit: newItemUnit.trim(),
      aisle: 'Other',
      checked: false
    };

    const updatedItems = [...list.items, newItem];
    await saveList(updatedItems);
    
    setNewItemName('');
    setNewItemAmount('');
    setNewItemUnit('');
    setAddDialogOpen(false);
    toast.success('Item added');
  };

  const copyToClipboard = async () => {
    // Demo mode - copy mock list
    if (isDemoMode && !isAuthenticated) {
      const text = list.items.map(item => 
        `${item.name}${item.amount ? ` - ${item.amount}` : ''}${item.unit ? ` ${item.unit}` : ''}`
      ).join('\n');
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
      return;
    }
    
    try {
      const response = await api.get(`/export/text/${id}`);
      await navigator.clipboard.writeText(response.data.text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const downloadList = async () => {
    // Demo mode - download mock list
    if (isDemoMode && !isAuthenticated) {
      const text = list.items.map(item => 
        `${item.name}${item.amount ? ` - ${item.amount}` : ''}${item.unit ? ` ${item.unit}` : ''}`
      ).join('\n');
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list.name.replace(/\s+/g, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('List downloaded');
      return;
    }
    
    try {
      const response = await api.get(`/export/text/${id}`);
      const blob = new Blob([response.data.text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list.name.replace(/\s+/g, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('List downloaded');
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const exportToStore = async (store) => {
    // Track export click
    trackExportClicked(store, isDemoMode);
    
    // Demo mode - show upgrade modal and mark export attempted
    if (isDemoMode && !isAuthenticated) {
      setHasTriedExport(true);
      setShowDemoUpgradeModal(true);
      trackDemoStepCompleted(4, 'Export to Store', { store: store });
      trackSignupModalShown('export_click', 4);
      return;
    }
    
    setExporting(store);
    try {
      const endpoint = store === 'instacart' ? '/export/instacart' 
        : store === 'amazon' ? '/export/amazon-fresh'
        : '/export/walmart';
      
      const response = await api.post(`${endpoint}?list_id=${id}`);
      window.open(response.data.url, '_blank');
      trackExportCompleted(store, list?.items?.length || 0);
      // Track checkout click for funnel analytics
      trackCheckoutClicked(store, list?.items?.length || 0);
      toast.success(`Opening ${store.charAt(0).toUpperCase() + store.slice(1)}...`);
    } catch (error) {
      // Check if upgrade required
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('UPGRADE_REQUIRED')) {
        setShowUpgradeModal(true);
      } else {
        toast.error(`Failed to export to ${store}`);
      }
    } finally {
      setExporting(null);
    }
  };

  // Shopping Mode Functions
  const startShoppingMode = async (store) => {
    // Demo mode - show upgrade modal and mark export attempted
    if (isDemoMode && !isAuthenticated) {
      setHasTriedExport(true);
      setShowDemoUpgradeModal(true);
      return;
    }
    
    // First check if user can export
    try {
      const endpoint = store === 'instacart' ? '/export/instacart' 
        : store === 'amazon' ? '/export/amazon-fresh'
        : '/export/walmart';
      
      // Try to get the export URL (this checks permissions)
      await api.post(`${endpoint}?list_id=${id}`);
      
      // If successful, start shopping mode
      setSelectedStore(store);
      setCurrentItemIndex(0);
      setAddedItems(new Set());
      setShoppingMode(true);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('UPGRADE_REQUIRED')) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Failed to start shopping mode');
      }
    }
  };

  const openCurrentItem = () => {
    if (!currentItem) return;
    const store = stores[selectedStore];
    // Just use item name for cleaner search results
    const searchTerm = encodeURIComponent(currentItem.name.trim());
    window.open(`${store.baseUrl}${searchTerm}`, '_blank');
  };

  const markItemAdded = () => {
    if (!currentItem) return;
    setAddedItems(prev => new Set([...prev, currentItemIndex]));
    
    if (currentItemIndex < uncheckedItems.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      // All items done
      toast.success('All items added to cart!');
      setShoppingMode(false);
    }
  };

  const skipItem = () => {
    if (currentItemIndex < uncheckedItems.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      toast.success('Shopping complete!');
      setShoppingMode(false);
    }
  };

  const closeShoppingMode = () => {
    setShoppingMode(false);
    setCurrentItemIndex(0);
    setAddedItems(new Set());
  };

  // In-Store Shopping Mode Functions
  const startInStoreMode = () => {
    if (!isProPlus) {
      setShowUpgradeModal(true);
      return;
    }
    setInStoreMode(true);
    setCheckoutComplete(false);
  };

  const exitInStoreMode = () => {
    setInStoreMode(false);
    setCheckoutComplete(false);
  };

  const checkoutToPantry = async () => {
    const checkedItems = list?.items?.filter(i => i.checked) || [];
    if (checkedItems.length === 0) {
      toast.error('No items checked off. Check items as you find them!');
      return;
    }

    setCheckingOut(true);
    try {
      // Call the pantry from-grocery-list endpoint
      const response = await api.post(`/pantry/from-grocery-list/${id}`);
      
      // 🎉 Trigger confetti celebration!
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { y: 0.6 },
        colors: ['#4A6C4C', '#43B02A', '#FFD700', '#4ECDC4']
      });
      
      toast.success(
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          <span><strong>{response.data.added} items</strong> added to your pantry!</span>
        </div>,
        { duration: 4000 }
      );
      
      setCheckoutComplete(true);
      
      // Optionally clear checked items from list
      const uncheckedOnly = list.items.filter(i => !i.checked);
      await saveList(uncheckedOnly);
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Pantry feature requires Pro Plus subscription');
        setShowUpgradeModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add items to pantry');
      }
    } finally {
      setCheckingOut(false);
    }
  };

  // Voice Shopping Functions
  // Check for Web Speech API support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!SpeechRecognition);
  }, []);

  // Find item by name (fuzzy matching)
  const findItemByName = useCallback((spokenName) => {
    if (!list?.items) return null;
    
    const normalizedSpoken = spokenName.toLowerCase().trim();
    
    // Try exact match first
    let matchIndex = list.items.findIndex(item => 
      item.name.toLowerCase() === normalizedSpoken
    );
    
    // Try partial match (spoken name contained in item name)
    if (matchIndex === -1) {
      matchIndex = list.items.findIndex(item => 
        item.name.toLowerCase().includes(normalizedSpoken)
      );
    }
    
    // Try reverse partial match (item name contained in spoken)
    if (matchIndex === -1) {
      matchIndex = list.items.findIndex(item => 
        normalizedSpoken.includes(item.name.toLowerCase())
      );
    }
    
    // Try word-by-word matching
    if (matchIndex === -1) {
      const spokenWords = normalizedSpoken.split(' ');
      matchIndex = list.items.findIndex(item => {
        const itemWords = item.name.toLowerCase().split(' ');
        return spokenWords.some(sw => itemWords.some(iw => iw.includes(sw) || sw.includes(iw)));
      });
    }
    
    return matchIndex !== -1 ? { item: list.items[matchIndex], index: matchIndex } : null;
  }, [list?.items]);

  // Process voice command
  const processVoiceCommand = useCallback((command) => {
    const lowerCommand = command.toLowerCase().trim();
    
    // "Check off [item]" or "Got [item]" or "Found [item]"
    const checkPatterns = [
      /^(?:check off|check|got|found|have|grabbed|picked up)\s+(.+)$/i,
      /^(.+)\s+(?:check|checked|done|got it)$/i
    ];
    
    for (const pattern of checkPatterns) {
      const match = lowerCommand.match(pattern);
      if (match) {
        const itemName = match[1].trim();
        const found = findItemByName(itemName);
        
        if (found && !found.item.checked) {
          toggleItem(found.index);
          toast.success(
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4" />
              <span>Checked off <strong>{found.item.name}</strong></span>
            </div>
          );
          return true;
        } else if (found && found.item.checked) {
          toast.info(`${found.item.name} is already checked off`);
          return true;
        } else {
          toast.error(`Couldn't find "${itemName}" in your list`);
          return false;
        }
      }
    }
    
    // "Uncheck [item]" or "Remove [item]"
    const uncheckPatterns = [
      /^(?:uncheck|remove|undo|put back)\s+(.+)$/i
    ];
    
    for (const pattern of uncheckPatterns) {
      const match = lowerCommand.match(pattern);
      if (match) {
        const itemName = match[1].trim();
        const found = findItemByName(itemName);
        
        if (found && found.item.checked) {
          toggleItem(found.index);
          toast.success(
            <div className="flex items-center gap-2">
              <X className="h-4 w-4" />
              <span>Unchecked <strong>{found.item.name}</strong></span>
            </div>
          );
          return true;
        } else if (found && !found.item.checked) {
          toast.info(`${found.item.name} isn't checked yet`);
          return true;
        }
      }
    }
    
    // "What's next?" or "Next item"
    if (/^(?:what'?s?\s*next|next\s*item|what\s*do\s*i\s*need)/.test(lowerCommand)) {
      const uncheckedItems = list?.items?.filter(i => !i.checked) || [];
      if (uncheckedItems.length > 0) {
        const nextItem = uncheckedItems[0];
        toast.info(
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4" />
            <span>Next: <strong>{nextItem.name}</strong> {nextItem.amount > 0 ? `(${nextItem.amount} ${nextItem.unit})` : ''}</span>
          </div>,
          { duration: 4000 }
        );
        // Also speak it if speech synthesis available
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(`Next item: ${nextItem.name}`);
          utterance.rate = 1.1;
          window.speechSynthesis.speak(utterance);
        }
        return true;
      } else {
        toast.success("All items checked! Ready to checkout.");
        return true;
      }
    }
    
    // "How many left?" or "Progress"
    if (/^(?:how\s*many\s*left|progress|status|remaining)/.test(lowerCommand)) {
      const uncheckedCount = list?.items?.filter(i => !i.checked).length || 0;
      const total = list?.items?.length || 0;
      toast.info(`${total - uncheckedCount} of ${total} items found. ${uncheckedCount} remaining.`);
      return true;
    }
    
    // Unknown command - try to interpret as item name
    const found = findItemByName(lowerCommand);
    if (found && !found.item.checked) {
      toggleItem(found.index);
      toast.success(
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4" />
          <span>Checked off <strong>{found.item.name}</strong></span>
        </div>
      );
      return true;
    }
    
    toast.error(
      <div>
        <p>Didn&apos;t understand: &quot;{command}&quot;</p>
        <p className="text-xs text-muted-foreground mt-1">Try: &quot;Check off milk&quot; or &quot;What&apos;s next?&quot;</p>
      </div>
    );
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findItemByName, list?.items]);

  // Start voice recognition
  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Voice recognition not supported in this browser. Try Chrome or Edge.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setTranscript('');
    };

    recognition.onresult = (event) => {
      const current = event.resultIndex;
      const result = event.results[current];
      const transcriptText = result[0].transcript;
      setTranscript(transcriptText);

      if (result.isFinal) {
        processVoiceCommand(transcriptText);
        setTranscript('');
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        toast.info('No speech detected. Tap the mic and try again.');
      } else if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      }
      setIsListening(false);
      setTranscript('');
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [processVoiceCommand]);

  // Stop voice recognition
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Group items by aisle
  const groupedItems = list?.items?.reduce((acc, item, index) => {
    const aisle = item.aisle || 'Other';
    if (!acc[aisle]) acc[aisle] = [];
    acc[aisle].push({ ...item, index });
    return acc;
  }, {}) || {};

  const checkedCount = list?.items?.filter(i => i.checked).length || 0;
  const totalCount = list?.items?.length || 0;
  const inStoreProgress = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <ShoppingCart className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <h2 className="text-xl font-medium mb-2">List not found</h2>
        <Button onClick={() => navigate('/grocery-lists')} variant="outline">
          Back to Lists
        </Button>
      </div>
    );
  }

  // Mobile handlers for MobileGroceryList component
  const handleMobileUpdateItem = async (index, updates) => {
    const updatedItems = [...list.items];
    updatedItems[index] = { ...updatedItems[index], ...updates };
    
    try {
      await api.put(`/grocery-lists/${id}`, {
        name: list.name,
        items: updatedItems
      });
      setList({ ...list, items: updatedItems });
      
      if (updates.checked) {
        trackGroceryItemChecked(isDemoMode);
      }
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleMobileAddItem = async (newItem) => {
    const updatedItems = [...list.items, newItem];
    
    try {
      await api.put(`/grocery-lists/${id}`, {
        name: list.name,
        items: updatedItems
      });
      setList({ ...list, items: updatedItems });
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleMobileExport = async (store) => {
    trackExportClicked(store, isDemoMode);
    
    try {
      const endpoint = store === 'instacart' ? '/export/instacart' 
        : store === 'amazon' ? '/export/amazon-fresh'
        : '/export/walmart';
      
      const response = await api.post(`${endpoint}?list_id=${id}`);
      window.open(response.data.url, '_blank');
      trackExportCompleted(store, list?.items?.length || 0);
      // Track checkout click for funnel analytics
      trackCheckoutClicked(store, list?.items?.length || 0);
      toast.success(`Opening ${store.charAt(0).toUpperCase() + store.slice(1)}...`);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error(`Failed to export to ${store}`);
      }
    }
  };

  // Render mobile version
  if (isMobile && isAuthenticated && !isDemoMode) {
    return (
      <>
        <MobileGroceryList
          list={list}
          listId={id}
          onUpdateItem={handleMobileUpdateItem}
          onAddItem={handleMobileAddItem}
          onExport={handleMobileExport}
          isPro={isProPlus}
        />
        <UpgradeModal 
          open={showUpgradeModal} 
          onOpenChange={setShowUpgradeModal}
          feature="Export to stores"
        />
      </>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen py-8" data-testid="grocery-list-detail-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate(isDemoMode ? '/grocery-lists?demo=true' : '/grocery-lists')} 
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Lists
          </Button>

          {/* Pro Upgrade Success Banner - Magic Moment */}
          {showUpgradeSuccess && isAuthenticated && (
            <Card className="border-primary/50 bg-gradient-to-r from-primary/10 via-yellow-500/10 to-primary/10 mb-6 overflow-hidden relative" data-testid="upgrade-success-banner">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(74,108,76,0.1),transparent_50%)]" />
              <CardContent className="py-5 relative">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center shadow-lg">
                    <Crown className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-foreground">You&apos;re all set!</h3>
                      <Badge className="bg-primary/20 text-primary border-0">Pro</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Your grocery list is being sent to Instacart now. Enjoy stress-free meal planning!
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowUpgradeSuccess(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Demo Mode Success Banner */}
          {isDemoMode && !isAuthenticated && (
            <Card className="border-accent/50 bg-accent/5 mb-6" data-testid="demo-list-ready-banner">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-accent-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Your grocery list is ready</p>
                    <p className="text-sm text-muted-foreground">Ingredients are automatically combined and organized for easy shopping.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-2">
                {list.name}
              </h1>
              <div className="flex items-center gap-4">
                <Badge variant="outline">
                  {checkedCount} / {totalCount} items
                </Badge>
                {checkedCount === totalCount && totalCount > 0 && (
                  <Badge className="bg-primary">Complete!</Badge>
                )}
              </div>
            </div>
          </div>

          {/* First-Time Tooltip for Demo */}
          {isDemoMode && !isAuthenticated && showFirstTimeTooltip && (
            <Card className="border-primary/30 bg-primary/5 mb-6" data-testid="demo-first-time-tooltip">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">Smart lists update automatically</p>
                    <p className="text-xs text-muted-foreground">Add or remove meals and your list adjusts instantly.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Card className="border-border/50 mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-3">
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="add-item-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display']">Add Item</DialogTitle>
                      <DialogDescription>
                        Add a new item to your grocery list
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <Input
                        placeholder="Item name (e.g., Milk)"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        data-testid="item-name-input"
                      />
                      <div className="flex gap-3">
                        <Input
                          placeholder="Amount"
                          type="number"
                          value={newItemAmount}
                          onChange={(e) => setNewItemAmount(e.target.value)}
                          className="w-24"
                          data-testid="item-amount-input"
                        />
                        <Input
                          placeholder="Unit (cups, lbs, etc.)"
                          value={newItemUnit}
                          onChange={(e) => setNewItemUnit(e.target.value)}
                          className="flex-1"
                          data-testid="item-unit-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={addItem} data-testid="confirm-add-item">
                        Add Item
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Button variant="outline" onClick={copyToClipboard} data-testid="copy-list-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>

                <Button variant="outline" onClick={downloadList} data-testid="download-list-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>

                {/* In-Store Shopping Mode Button (Pro Plus) */}
                {isAuthenticated && !isDemoMode && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="outline" 
                        onClick={startInStoreMode}
                        className={isProPlus ? "border-primary/50 hover:bg-primary/5" : ""}
                        data-testid="in-store-shopping-btn"
                      >
                        <ShoppingBag className="h-4 w-4 mr-2" />
                        In-Store
                        {!isProPlus && <Lock className="h-3 w-3 ml-1 text-muted-foreground" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isProPlus 
                        ? "Check off items as you shop, then add to pantry"
                        : "Pro Plus feature - Upgrade to unlock"
                      }
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Store Export Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-primary" data-testid="export-store-btn">
                      {exporting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Store className="h-4 w-4 mr-2" />
                      )}
                      {isDemoMode ? 'Send to Store' : 'Shop Online'}
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      {isDemoMode ? 'Send to Instacart' : 'Quick Add (Item by Item)'}
                    </div>
                    <DropdownMenuItem 
                      onClick={() => startShoppingMode('instacart')}
                      className="cursor-pointer"
                      data-testid="shopping-mode-instacart"
                    >
                      <div className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-primary" />
                        <div className="h-5 w-5 rounded bg-[#43B02A] flex items-center justify-center">
                          <span className="text-white text-xs font-bold">i</span>
                        </div>
                        {isDemoMode ? 'Send to Instacart' : 'Start with Instacart'}
                      </div>
                    </DropdownMenuItem>
                    
                    <div className="my-1 h-px bg-border" />
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      {isDemoMode ? 'Copy to another store' : 'More Stores'}
                    </div>
                    <DropdownMenuItem 
                      onClick={() => startShoppingMode('amazon')}
                      className="cursor-pointer"
                      data-testid="shopping-mode-amazon"
                    >
                      <div className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-primary" />
                        <div className="h-5 w-5 rounded bg-[#FF9900] flex items-center justify-center">
                          <span className="text-white text-xs font-bold">a</span>
                        </div>
                        Amazon Fresh
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => startShoppingMode('walmart')}
                      className="cursor-pointer"
                      data-testid="shopping-mode-walmart"
                    >
                      <div className="flex items-center gap-2">
                        <Play className="h-4 w-4 text-primary" />
                        <div className="h-5 w-5 rounded bg-[#0071CE] flex items-center justify-center">
                          <span className="text-white text-xs font-bold">W</span>
                        </div>
                        Walmart
                      </div>
                    </DropdownMenuItem>
                    
                    {/* Microcopy */}
                    <div className="my-1 h-px bg-border" />
                    <div className="px-2 py-2 text-xs text-muted-foreground text-center">
                      Works with most major grocery stores.
                    </div>
                    <div className="px-2 pb-2">
                      <button 
                        onClick={() => navigate('/dashboard?settings=shopping')}
                        className="w-full text-xs text-primary/70 hover:text-primary transition-colors text-center"
                        data-testid="adjust-preferences-link"
                      >
                        Adjust shopping preferences →
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>

          {/* Demo Mode Step 4 Banner - "Shop once. Cook all week." */}
          {isDemoMode && !isAuthenticated && (
            <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5 mb-6" data-testid="demo-step4-export-callout">
              <CardContent className="py-5">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-primary">Step 4</Badge>
                    <span className="font-semibold text-lg text-foreground">Shop once. Cook all week.</span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-[52px]">Send your grocery list to Instacart or your favorite store in one click.</span>
                  <span className="text-xs text-muted-foreground ml-[52px]">No re-typing. No missed ingredients.</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Items by Aisle */}
          {Object.keys(groupedItems).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedItems).map(([aisle, items]) => (
                <Card key={aisle} className="border-border/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      {aisle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <div className="space-y-2">
                      {items.map((item) => (
                        <div 
                          key={item.index}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 group"
                          data-testid={`item-${item.index}`}
                        >
                          <Checkbox
                            checked={item.checked}
                            onCheckedChange={() => toggleItem(item.index)}
                            data-testid={`item-checkbox-${item.index}`}
                          />
                          <div className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                            <span className="font-medium">{item.name}</span>
                            {(item.amount > 0 || item.unit) && (
                              <span className="text-muted-foreground ml-2">
                                {item.amount > 0 && item.amount} {item.unit}
                              </span>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                            onClick={() => removeItem(item.index)}
                            data-testid={`remove-item-${item.index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <ShoppingCart className="h-12 w-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground mb-4">This list is empty</p>
                <Button onClick={() => setAddDialogOpen(true)} data-testid="empty-add-item-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Item
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Smart Suggestions - Pro Plus */}
          {isProPlus && !isDemoMode && suggestions.length > 0 && showSuggestions && (
            <Card className="border-primary/30 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20 mt-6" data-testid="smart-suggestions-card">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                      <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-medium">You might also need...</CardTitle>
                      <p className="text-xs text-muted-foreground">AI-powered suggestions based on your list</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowSuggestions(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="grid gap-2">
                  {suggestions.map((suggestion, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/30 transition-colors"
                      data-testid={`suggestion-${idx}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{suggestion.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {suggestion.category}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{suggestion.reason}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="ml-2 h-8"
                        onClick={() => addSuggestionToList(suggestion)}
                        data-testid={`add-suggestion-${idx}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full mt-3 text-muted-foreground"
                  onClick={loadSuggestions}
                  disabled={loadingSuggestions}
                >
                  {loadingSuggestions ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3 mr-2" />
                  )}
                  Get more suggestions
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* Loading Suggestions */}
          {isProPlus && !isDemoMode && loadingSuggestions && suggestions.length === 0 && (
            <Card className="border-border/50 mt-6">
              <CardContent className="py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                <p className="text-sm text-muted-foreground">Finding items you might need...</p>
              </CardContent>
            </Card>
          )}

          {/* Skipped Pantry Staples - Passive Transparency */}
          {skippedStaples.length > 0 && !isDemoMode && (
            <TooltipProvider>
              <div className="mt-6" data-testid="skipped-staples-section">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setShowSkippedStaples(!showSkippedStaples)}
                      className="flex items-center gap-2 text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors w-full"
                    >
                      <Package className="h-3.5 w-3.5" />
                      <span>Pantry staples (skipped)</span>
                      {showSkippedStaples ? (
                        <ChevronUp className="h-3.5 w-3.5 ml-auto" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 ml-auto" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-xs">Pantry items are excluded to avoid duplicates. You can change this anytime.</p>
                  </TooltipContent>
                </Tooltip>
                
                {showSkippedStaples && (
                  <p className="text-xs text-muted-foreground/60 mt-2 pl-5">
                    {skippedStaples.slice(0, 8).join(' · ')}
                    {skippedStaples.length > 8 && ` · +${skippedStaples.length - 8} more`}
                  </p>
                )}
              </div>
            </TooltipProvider>
          )}

          {/* Sync Info for Demo */}
          {isDemoMode && !isAuthenticated && list?.items?.length > 0 && !showStep4Complete && !hasTriedExport && (
            <p className="text-xs text-muted-foreground text-center mt-6">
              Update your meal plan anytime — this list stays in sync.
            </p>
          )}

          {/* Step 4 Complete Banner - shows after export click or scroll */}
          {isDemoMode && !isAuthenticated && (showStep4Complete || hasTriedExport) && (
            <Card className="border-primary/50 bg-gradient-to-r from-primary/10 to-accent/10 mt-8" data-testid="demo-step4-complete-banner">
              <CardContent className="py-8">
                <div className="text-center">
                  {/* Success State */}
                  <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold font-['Playfair_Display'] mb-2">
                    You&apos;re all set!
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    Groceries are ready — now enjoy stress-free meals all week.
                  </p>
                  <p className="text-sm text-muted-foreground mb-8">
                    You can come back anytime to adjust your plan or list.
                  </p>

                  {/* Final CTA */}
                  <div className="border-t border-border pt-6">
                    <p className="text-lg font-medium mb-4">
                      Ready to make this your weekly routine?
                    </p>
                    <Link to={`/upgrade?returnTo=${encodeURIComponent(`/grocery-lists/${id}`)}&listId=${id}`}>
                      <Button size="lg" className="px-8" data-testid="demo-final-signup-cta">
                        Start Planning Free
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                    <p className="text-xs text-muted-foreground mt-3">
                      No credit card required.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {saving && (
            <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </div>
          )}

          {/* Shopping Mode Overlay */}
          {shoppingMode && currentItem && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <Card className="w-full max-w-lg border-border/50 shadow-2xl">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-8 w-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: stores[selectedStore].color }}
                      >
                        <Store className="h-4 w-4 text-white" />
                      </div>
                      <CardTitle className="font-['Playfair_Display'] text-lg">
                        Shopping Mode
                      </CardTitle>
                    </div>
                    <Button variant="ghost" size="icon" onClick={closeShoppingMode} data-testid="close-shopping-mode">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                      <span>Progress</span>
                      <span>{addedItems.size} of {uncheckedItems.length} items</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  {/* Current Item Display */}
                  <div className="bg-muted/50 rounded-xl p-6 mb-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">Add to cart:</p>
                    <p className="text-2xl font-semibold font-['Playfair_Display'] mb-1">
                      {currentItem.name}
                    </p>
                    {(currentItem.amount > 0 || currentItem.unit) && (
                      <p className="text-muted-foreground">
                        {currentItem.amount > 0 && currentItem.amount} {currentItem.unit}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button 
                      className="w-full h-12 text-base"
                      style={{ backgroundColor: stores[selectedStore].color }}
                      onClick={openCurrentItem}
                      data-testid="open-item-btn"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Search on {stores[selectedStore].name}
                    </Button>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        variant="outline" 
                        className="h-12"
                        onClick={skipItem}
                        data-testid="skip-item-btn"
                      >
                        <SkipForward className="h-4 w-4 mr-2" />
                        Skip
                      </Button>
                      <Button 
                        className="h-12 bg-green-600 hover:bg-green-700"
                        onClick={markItemAdded}
                        data-testid="item-added-btn"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Added to Cart
                      </Button>
                    </div>
                  </div>

                  {/* Remaining Items Preview */}
                  {uncheckedItems.length > 1 && (
                    <div className="mt-6 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Coming up next:</p>
                      <div className="flex flex-wrap gap-2">
                        {uncheckedItems.slice(currentItemIndex + 1, currentItemIndex + 4).map((item, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {item.name}
                          </Badge>
                        ))}
                        {uncheckedItems.length - currentItemIndex - 1 > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{uncheckedItems.length - currentItemIndex - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* In-Store Shopping Mode Overlay */}
          {inStoreMode && (
            <div className="fixed inset-0 bg-background z-50 flex flex-col" data-testid="in-store-mode-overlay">
              {/* Header */}
              <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0">
                <div className="max-w-3xl mx-auto px-4 py-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ShoppingBag className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-semibold font-['Playfair_Display']">In-Store Shopping</h2>
                        <p className="text-xs text-muted-foreground">Check items as you find them</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Voice Button */}
                      {voiceSupported && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant={isListening ? "default" : "outline"}
                              size="icon"
                              onClick={isListening ? stopListening : startListening}
                              className={isListening ? "bg-red-500 hover:bg-red-600 animate-pulse" : ""}
                              data-testid="voice-shopping-btn"
                            >
                              {isListening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {isListening ? "Stop listening" : "Voice commands (tap to talk)"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Button variant="ghost" size="icon" onClick={exitInStoreMode} data-testid="exit-in-store-btn">
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Voice Listening Indicator */}
                  {isListening && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-red-500 flex items-center justify-center animate-pulse">
                          <Mic className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-600">Listening...</p>
                          {transcript ? (
                            <p className="text-sm text-muted-foreground">&quot;{transcript}&quot;</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">Say &quot;check off [item]&quot; or &quot;what&apos;s next?&quot;</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{checkedCount} of {totalCount} items found</span>
                    </div>
                    <Progress value={inStoreProgress} className="h-2" />
                  </div>
                </div>
              </div>

              {/* Checkout Complete State */}
              {checkoutComplete ? (
                <div className="flex-1 flex items-center justify-center p-4">
                  <Card className="w-full max-w-md border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
                    <CardContent className="pt-8 pb-6 text-center">
                      <div className="h-16 w-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-2">
                        Shopping Complete!
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        Your items have been added to your pantry with smart expiration dates.
                      </p>
                      <div className="space-y-3">
                        <Button onClick={() => navigate('/dashboard')} className="w-full" data-testid="go-to-pantry-btn">
                          <Package className="h-4 w-4 mr-2" />
                          View My Pantry
                        </Button>
                        <Button variant="outline" onClick={exitInStoreMode} className="w-full">
                          Back to List
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <>
                  {/* Items List */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="max-w-3xl mx-auto px-4 py-6">
                      {Object.keys(groupedItems).length > 0 ? (
                        <div className="space-y-6">
                          {Object.entries(groupedItems).map(([aisle, items]) => (
                            <div key={aisle}>
                              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
                                {aisle}
                              </h3>
                              <div className="space-y-2">
                                {items.map((item) => (
                                  <div 
                                    key={item.index}
                                    onClick={() => toggleItem(item.index)}
                                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                                      item.checked 
                                        ? 'bg-primary/5 border-primary/30' 
                                        : 'bg-background border-border hover:border-primary/20'
                                    }`}
                                    data-testid={`in-store-item-${item.index}`}
                                  >
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                                      item.checked ? 'bg-primary text-white' : 'bg-muted'
                                    }`}>
                                      {item.checked ? (
                                        <Check className="h-5 w-5" />
                                      ) : (
                                        <span className="text-sm font-medium text-muted-foreground">
                                          {item.index + 1}
                                        </span>
                                      )}
                                    </div>
                                    <div className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                                      <span className="font-medium text-lg">{item.name}</span>
                                      {(item.amount > 0 || item.unit) && (
                                        <span className="text-muted-foreground ml-2">
                                          {item.amount > 0 && item.amount} {item.unit}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <ShoppingCart className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                          <p className="text-muted-foreground">No items in this list</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Checkout Footer */}
                  <div className="border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky bottom-0">
                    <div className="max-w-3xl mx-auto px-4 py-4">
                      <Button 
                        className="w-full h-14 text-lg"
                        onClick={checkoutToPantry}
                        disabled={checkingOut || checkedCount === 0}
                        data-testid="checkout-to-pantry-btn"
                      >
                        {checkingOut ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Adding to Pantry...
                          </>
                        ) : checkedCount === 0 ? (
                          <>
                            <ShoppingBag className="h-5 w-5 mr-2" />
                            Check items to continue
                          </>
                        ) : (
                          <>
                            <Package className="h-5 w-5 mr-2" />
                            Checkout & Add {checkedCount} Items to Pantry
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        Checked items will be removed from your list and added to your pantry
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Upgrade Modal (for authenticated free users) */}
          <UpgradeModal 
            open={showUpgradeModal} 
            onClose={() => setShowUpgradeModal(false)}
            feature="store_export"
          />

          {/* Demo Upgrade Modal */}
          <Dialog open={showDemoUpgradeModal} onOpenChange={(open) => {
            setShowDemoUpgradeModal(open);
            // Show Step 4 complete when closing modal
            if (!open && hasTriedExport) {
              setShowStep4Complete(true);
            }
          }}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle className="font-['Playfair_Display'] text-xl">
                  Finish your plan the easy way
                </DialogTitle>
                <p className="text-sm text-primary font-medium mt-1">
                  Send your grocery list to Instacart or your favorite store in one click.
                </p>
                <DialogDescription className="mt-3">
                  Upgrade to keep planning meals without limits.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span>One-click grocery exports</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span>Lists that stay in sync</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm">
                    <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span>Unlimited meal planning</span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <Link to={`/upgrade?returnTo=${encodeURIComponent(`/grocery-lists/${id}`)}&listId=${id}`} className="w-full">
                  <Button className="w-full" size="lg" data-testid="demo-upgrade-modal-cta">
                    <Zap className="h-4 w-4 mr-2" />
                    Create account & upgrade
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowDemoUpgradeModal(false)}
                  data-testid="demo-continue-planning"
                >
                  Continue planning
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Cancel anytime
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default GroceryListDetail;
